<template>
  <div class="page-company-users">
    <PageHeader>
      <div class="heading">
        <h1>
          <strong class="name">{{ company.name }}</strong> / Team
        </h1>
      </div>

      <div class="header-actions">
        <Permissions
          :permissions="currentUser.permissions"
          :requireAll="{company_user: ['create']}"
        >
          <Btn tertiary @click="addUser">
            Invite a team member
          </Btn>
        </Permissions>
      </div>
    </PageHeader>

    <div class="page-columns">
      <div class="col main">
        <div class="page-content">
          <Notification
            v-if="notification.active"
            :content="notification.content"
            :type="notification.type"
          />

          <Permissions
            :permissions="currentUser.permissions"
            :requireAll="{company_user: ['list']}"
          >
            <section v-if="hasUsers">
              <table-data
                :rows="companyUsers"
                :headings="[
                  {key: 'edit', heading: 'Email Address'},
                  {key: 'resend', heading: ''},
                  {key: 'user-active-status', heading: 'Active Status'}
                ]"
              >
                <div
                  v-for="(user, index) in companyUsers"
                  :slot="'td-' + index + '-edit'"
                  :key="index"
                >
                  <router-link
                    :to="{
                      name: 'company-user',
                      params: {companyId: companyId, userId: user.id}
                    }"
                  >
                    {{ user.email }}
                    <span class="is-owner" v-if="user.owns_company">owner</span>
                  </router-link>
                </div>

                 <div
                  v-for="(user, index) in companyUsers"
                  :slot="'td-' + index + '-resend'"
                  :key="index"
                >
                  <div
                    class="resend-link"
                    v-if="!user.active"
                    @click="resendInvite(user)">
                    Resend Invite
                  </div>
                </div>

                <div
                  v-for="(user, index) in companyUsers"
                  :slot="'td-' + index + '-user-active-status'"
                  :key="index"
                  class="user-active-status"
                >
                  <div
                    class="active-state"
                    :class="{'is-active': user.active}"
                  >
                  </div>
                </div>
              </table-data>

              <pagination @changePage="changePage" :pagination="pageData">
              </pagination>
            </section>
          </Permissions>
        </div>
      </div>

      <div class="col side">
        <PageSummary>
          <div>
            <h1>Your Team</h1>
            <p>
              The dashboard presents a list of websites within
              <strong>{{ company.name }}</strong
              >, as well as users within this company.
            </p>
            <p>
              From here, you can access the powerful site builder to modify each
              of your websites.
            </p>
          </div>
        </PageSummary>
      </div>
    </div>

    <router-view :companyName="company.name" />
  </div>
</template>

<script>
import page from '@/mixins/page'
import pagination from '@/mixins/pagination'
import notification from '@/mixins/notification'

import {mapState} from 'vuex'

import {readCompanyData} from '@/lib/companies'
import {
  resendCompanyUserData,
  readCompanyUsersData
} from '@/lib/company_users'

import Table from '@/components/Table'
import PageHeader from '@/components/PageHeader'
import PageSummary from '@/components/PageSummary'
import Permissions from '@/components/Permissions'
import Btn from '@/components/Btn'

export default {
  name: 'company-users',

  mixins: [page, pagination, notification],

  props: {
    companyId: {
      type: String,
      required: true
    }
  },

  components: {
    TableData: Table,
    PageHeader,
    PageSummary,
    Permissions,
    Btn
  },

  data() {
    return {
      company: {},
      companyUsers: []
    }
  },

  methods: {
    getData() {
      readCompanyData(this.companyId).then(({data}) => {
        this.company = data
      })

      readCompanyUsersData(this.companyId, this.pagination).then(
        ({data, page}) => {
          this.companyUsers = data
          this.pageData = page
        }
      )
    },

    resendInvite(user) {
      resendCompanyUserData(this.companyId, user.id).then(({data}) => {
        this.triggerNotification('success', 'invite', 'sent')
      })
    },

    addUser() {
      this.$router.push({name: 'add-company-user'})
    }
  },

  computed: {
    ...mapState(['currentUser']),

    title() {
      return `Team ${this.company.name}`
    },

    hasUsers() {
      return !!this.companyUsers.length
    }
  },

  mounted() {
    this.getData()
  },

  watch: {
    $route() {
      this.getData()
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.page-company-users
  @extend %page

  .resend-link
    font-weight: bold
    cursor: pointer
    text-transform: uppercase
    opacity: 0.25

  .is-owner
    background: rgba(0, 0, 0, 0.1)
    padding: 4px
    border-radius: 4px
    text-transform: uppercase
    font-weight: bold
    font-size: 9px
</style>
